import * as Sentry from "@sentry/react"
import { StripeError } from "@stripe/stripe-js"

// Every confirmPayment error used to be rethrown unless it was a card or
// validation error: the user saw no message, and Sentry an unhandled
// rejection (PRODUCTION-A, -T0): e.g. a failed 3-D Secure
// authentication comes back as an invalid_request_error.
export function getConfirmPaymentErrorMessage(
  error: StripeError,
  t: (key: string) => string,
): string {
  if (error.type === "card_error" || error.type === "validation_error") {
    return error.message ?? t("components.checkoutForm.somethingWentWrong")
  }

  if (error.code === "payment_intent_authentication_failure") {
    return t("components.checkoutForm.paymentFailed")
  }

  Sentry.captureException(
    new Error(`confirmPayment failed: ${error.type} ${error.code ?? ""}`),
    { extra: { declineCode: error.decline_code, docUrl: error.doc_url } },
  )

  return t("components.checkoutForm.somethingWentWrong")
}

/**
 * The Payment Element refuses a PaymentIntent that already succeeded or was
 * canceled (PRODUCTION-W1): the client secret is in the URL, so the browser's
 * back button, a reload or a history entry opens the payment page for a
 * finished payment.
 */
export function isTerminalPaymentIntentError(error: {
  message?: string
}): boolean {
  return /terminal state/i.test(error.message ?? "")
}

/** Where a payment page opened for a finished PaymentIntent should go. */
export function getFinishedPaymentIntentTarget(
  status: string | undefined,
): "completion" | "plans" | undefined {
  if (status === "succeeded") return "completion"
  if (status === "canceled") return "plans"

  return undefined
}

/**
 * A PaymentIntent that was submitted but isn't settled (bank debits, some
 * wallets): the Payment Element can't take it and completion has nothing to
 * record yet, so the payment page waits for it.
 */
export function isPendingPaymentIntentStatus(
  status: string | undefined,
): boolean {
  return status === "processing" || status === "requires_capture"
}
